import { Component, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { ConfirmationModalComponent, FormActionButtonsComponent, FormInputComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { Language, PageMode } from "../../../shared/constants/enums";
import { ValidatorModelType } from "../../../shared/models";
import {
  AppService,
  OtherEntitiesApiService,
  SystemLayoutService,
  ValidatorApiService,
} from "../../../shared/services";

@Component({
  selector: "adaa-other-entity-editor",
  imports: [ReactiveFormsModule, RouterModule, TranslatePipe, FormActionButtonsComponent, FormInputComponent],
  templateUrl: "./other-entity-editor.html",
  styleUrl: "./other-entity-editor.scss",
})
export class OtherEntityEditorComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _toastrService = inject(ToastrService);
  private _translateService = inject(TranslateService);
  private _otherEntitiesApiService = inject(OtherEntitiesApiService);
  private _router = inject(Router);
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _activatedRoute = inject(ActivatedRoute);
  private _validatorApiService = inject(ValidatorApiService);
  private _systemLayoutService = inject(SystemLayoutService);

  id = input<string>();
  pageMode = input.required<string>();

  otherEntitiyForm: FormGroup = new FormGroup({});
  AdaaHelper = AdaaHelper;
  otherEntityId: string | null;

  PageMode = PageMode;

  submitted = signal<boolean>(false);
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  public get displayLabel(): string {
    const nameEN = this.otherEntitiyForm.get("nameEN")?.value;
    const nameAE = this.otherEntitiyForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    const id = this.id();
    if (id) this._getOtherEntity(+id);

    this._prepareForm();

    this._systemLayoutService.hasActiveEntityChanged$.subscribe(() => {
      !AdaaHelper.isPMOEntity() && this._router.navigateByUrl("/console");
    });
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.otherEntitiyForm = this._formBuilder.group({
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      descriptionAE: { value: null, disabled: isViewMode },
      descriptionEN: { value: null, disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_OTHER_ENTITIES, this.otherEntitiyForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.otherEntitiyForm = response.form;
        },
      });
  }

  private _getOtherEntity(otherEntityId: number) {
    this._otherEntitiesApiService.getOtherEntityById(otherEntityId).subscribe({
      next: (response) => {
        this.otherEntitiyForm.patchValue({ ...response.responseData });
        this.otherEntitiyForm.markAsPristine();
        this.otherEntitiyForm.markAsUntouched();
      },
    });
  }

  public checkInvalid(control: string) {
    return (
      this.otherEntitiyForm.controls[control].errors?.["required"] &&
      (this.otherEntitiyForm.get(`${control}`)?.dirty || this.submitted())
    );
  }

  public updateOtherEntity() {
    this.submitted.set(true);
    if (this.otherEntitiyForm.invalid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }
    const otherEntity = this._formatOtherEntity();

    const serviceUrl =
      this.pageMode() === PageMode.edit
        ? this._otherEntitiesApiService.updateOtherEntity(otherEntity)
        : this._otherEntitiesApiService.createOtherEntity(otherEntity);

    serviceUrl.subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigateByUrl("/console/other-entities/list");
      },
    });
  }

  private _formatOtherEntity() {
    const otherEntity = this.otherEntitiyForm.value;
    otherEntity.status = Constants.OBJECT_STATUS.ACTIVE;
    if (this.pageMode() === PageMode.edit) {
      otherEntity.updateTS = Date.now();
      otherEntity.id = +this.id()!;
    }
    return otherEntity;
  }

  public cancelForm() {
    if (this.otherEntitiyForm.dirty) {
      const modal = this._modalService.open(ConfirmationModalComponent, {
        centered: true,
        size: "md",
        modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
      });

      modal.componentInstance.header = "common.form.modals_leave.cancel_yes_no_title";
      modal.componentInstance.title = "common.form.modals_leave.cancel_yes_no_information";

      modal.result.then((e) => {
        if (e) this._router.navigateByUrl(`/console/other-entities//list`);
      });
    } else this._router.navigateByUrl(`/console/other-entities//list`);
  }
}
